import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Check,
  Crown,
  Zap,
  Star,
  Shield,
  CreditCard,
  AlertCircle,
  Loader,
  Smartphone,
} from "lucide-react";
import { getPaymentPlans } from "../apis/payment";
import useRenewalPayment from "../hooks/useRenewalPayment";

// Razorpay Script Loader Component
const RazorpayScript = ({ onLoad, onError }) => {
  useEffect(() => {
    if (window.Razorpay) {
      onLoad && onLoad();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => onLoad && onLoad();
    script.onerror = () => onError && onError("Failed to load Razorpay SDK");
    document.head.appendChild(script);
    return () => {
      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );
      if (existingScript?.parentNode) existingScript.parentNode.removeChild(existingScript);
    };
  }, [onLoad, onError]);
  return null;
};

const RenewalPaymentModal = ({
  open,
  onClose,
  onPaymentSuccess,
  instanceId,
  instanceName,
  userInfo = {},
}) => {
  const [selectedPlan, setSelectedPlan] = useState("6_months");
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(null);

  const plansCache = useRef(null);
  const { processRenewalPayment, isProcessing, error, clearError } = useRenewalPayment();

  const planDetails = {
    free_trial: {
      icon: Zap,
      color: "green",
      popular: false,
      badge: "Free Trial",
      features: [
        "WhatsApp Business API Access",
        "Unlimited Messages",
        "Basic Analytics",
        "Email Support",
        "3 Days Active",
        "No Payment Required",
      ],
      savings: null,
    },
    "1_month": {
      icon: Zap,
      color: "blue",
      popular: false,
      badge: null,
      features: [
        "WhatsApp Business API Access",
        "Unlimited Messages",
        "Basic Analytics",
        "Email Support",
        "30 Days Active",
      ],
      savings: null,
    },
    "6_months": {
      icon: Crown,
      color: "purple",
      popular: true,
      badge: "Best Value",
      features: [
        "WhatsApp Business API Access",
        "Unlimited Messages",
        "Advanced Analytics",
        "Priority Support",
        "180 Days Active",
        "Save 45% vs Monthly",
      ],
      savings: "45%",
    },
    "12_months": {
      icon: Star,
      color: "gold",
      popular: false,
      badge: "Maximum Savings",
      features: [
        "WhatsApp Business API Access",
        "Unlimited Messages",
        "Premium Analytics",
        "24/7 Priority Support",
        "365 Days Active",
        "Save 60% vs Monthly",
      ],
      savings: "60%",
    },
  };

  useEffect(() => {
    if (window.Razorpay) setRazorpayLoaded(true);
  }, []);

  const fetchPlans = useCallback(async () => {
    if (plansCache.current) {
      setPlans(plansCache.current);
      return;
    }
    try {
      setIsLoading(true);
      const result = await getPaymentPlans();
      if (result.success) {
        setPlans(result.data);
        plansCache.current = result.data;
      } else {
        throw new Error(result.message || "Failed to fetch plans");
      }
    } catch (err) {
      console.error("Failed to fetch plans:", err);
      setScriptError("Failed to load payment plans. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPlans();
      clearError && clearError();
      setScriptError(null);
    }
  }, [open, fetchPlans, clearError]);

  useEffect(() => {
    if (!open) {
      plansCache.current = null;
      setPlans([]);
      setScriptError(null);
    }
  }, [open]);

  const handlePayment = async () => {
    if (!selectedPlan || !instanceId) return;
    if (selectedPlan !== "free_trial" && !razorpayLoaded) {
      setScriptError("Payment system is loading. Please wait...");
      return;
    }
    try {
      const result = await processRenewalPayment(selectedPlan, instanceId, userInfo);
      onPaymentSuccess(result.data.subscription);
      onClose();
    } catch (err) {
      console.error("Payment error:", err);
    }
  };

  const handleRazorpayLoad = useCallback(() => {
    setRazorpayLoaded(true);
    setScriptError(null);
  }, []);
  const handleRazorpayError = useCallback((msg) => {
    setScriptError(msg);
    setRazorpayLoaded(false);
  }, []);
  const handleErrorClear = useCallback(() => {
    clearError && clearError();
    setScriptError(null);
  }, [clearError]);

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-300",
        text: "text-blue-700",
        pill: "bg-blue-100 text-blue-700",
      },
      purple: {
        bg: "bg-violet-50",
        border: "border-violet-300",
        text: "text-violet-700",
        pill: "bg-violet-100 text-violet-700",
      },
      gold: {
        bg: "bg-amber-50",
        border: "border-amber-300",
        text: "text-amber-700",
        pill: "bg-amber-100 text-amber-700",
      },
      green: {
        bg: "bg-[#01CC54]/10",
        border: "border-[#01CC54]/30",
        text: "text-[#01CC54]",
        pill: "bg-[#01CC54]/15 text-[#01CC54]",
      },
    };
    return colors[color] || colors.blue;
  };

  const selectedPlanData = plans.find((p) => p.id === selectedPlan);
  const isFreeTrialSelected = selectedPlan === "free_trial";

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        {!isFreeTrialSelected && !razorpayLoaded && !scriptError && (
          <RazorpayScript onLoad={handleRazorpayLoad} onError={handleRazorpayError} />
        )}

        <motion.div
          className="max-w-6xl w-full max-h-[95vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white backdrop-blur-md shadow-2xl ring-1 ring-gray-100"
          initial={{ scale: 0.96, opacity: 0, y: 14 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 14 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-4 pb-3 border-b border-gray-200 bg-gradient-to-br from-white via-gray-50/30 to-white rounded-t-2xl">
            <button
              onClick={onClose}
              className="absolute top-2 right-2 p-1.5 rounded-full text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40 transition"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <div className="text-center">
              <motion.div
                className="rounded-full p-3 w-fit mx-auto mb-2 ring-2 ring-[#01CC54]/20 bg-gradient-to-br from-[#01CC54]/10 to-[#01CC54]/5 shadow-lg"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: "spring" }}
              >
                <Smartphone size={24} className="text-[#01CC54]" />
              </motion.div>
              <motion.h2
                className="text-xl lg:text-2xl font-extrabold text-gray-900 mb-1.5"
                initial={{ y: -12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 }}
              >
                Choose Your <span className="text-[#01CC54]">Renewal Plan</span>
              </motion.h2>
              <motion.div
                className="rounded-xl px-3 py-1.5 inline-block mb-1.5 bg-gradient-to-r from-[#01CC54]/10 to-[#048AAD]/10 border border-[#01CC54]/20 shadow-sm"
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
              >
                <p className="text-gray-900 font-bold text-sm">
                  Instance: <span className="text-[#01CC54]">"{instanceName}"</span>
                </p>
              </motion.div>
              <motion.p
                className="text-gray-600 text-xs"
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Select a plan duration to renew your WhatsApp instance
              </motion.p>
            </div>
          </div>

          {/* Error Display */}
          <AnimatePresence>
            {(error || scriptError) && (
              <motion.div
                className="mx-4 mt-3 p-3 rounded-lg flex items-center border border-rose-300 bg-rose-50 text-rose-700"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <AlertCircle className="mr-2 flex-shrink-0" size={18} />
                <p className="flex-1 text-sm">{error || scriptError}</p>
                <button
                  onClick={handleErrorClear}
                  className="ml-auto text-rose-700 hover:text-rose-800 transition-colors"
                >
                  <X size={14} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Razorpay load banner */}
          {!isFreeTrialSelected && !razorpayLoaded && !scriptError && (
            <motion.div
              className="mx-4 mt-3 p-3 rounded-lg flex items-center border border-blue-200 bg-blue-50 text-blue-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Loader className="mr-2 animate-spin" size={18} />
              <p className="text-sm">Loading payment system...</p>
            </motion.div>
          )}

          <div className="p-3">
            {/* Loading state */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl p-4 border border-gray-200 bg-gray-50 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded mb-3"></div>
                    <div className="h-10 bg-gray-200 rounded mb-3"></div>
                    <div className="space-y-1.5">
                      {[1, 2, 3, 4, 5].map((j) => (
                        <div key={j} className="h-3 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {plans.map((plan, index) => {
                  const details = planDetails[plan.id];
                  if (!details) return null;
                  const colors = getColorClasses(details.color);
                  const Icon = details.icon;
                  const isSelected = selectedPlan === plan.id;

                  return (
                    <motion.div
                      key={plan.id}
                      className={`relative rounded-2xl p-4 cursor-pointer transition-all duration-300 border-2 ${
                        isSelected
                          ? `${colors.border} ${colors.bg} shadow-2xl scale-[1.02] ring-4 ring-[#01CC54]/30`
                          : "border-gray-200 hover:border-[#01CC54]/60 hover:shadow-xl bg-white hover:scale-[1.01]"
                      }`}
                      onClick={() => setSelectedPlan(plan.id)}
                      whileHover={{ y: -8, scale: isSelected ? 1.02 : 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
                      layout
                    >
                      {/* Badge */}
                      {(details.popular || details.badge) && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                          <motion.span
                            className={`px-3 py-0.5 rounded-full text-xs font-bold shadow-xl ${colors.pill} border-2 ${colors.border} uppercase tracking-wide`}
                            initial={{ scale: 0, rotate: -12 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                          >
                            {details.badge || "Most Popular"}
                          </motion.span>
                        </div>
                      )}

                      {/* Icon and Selection Indicator */}
                      <div className="flex items-center justify-between mb-3 mt-1">
                        <motion.div
                          className={`p-2 rounded-xl ${colors.bg} border-2 ${colors.border} shadow-lg`}
                          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.15 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Icon size={22} className={colors.text} />
                        </motion.div>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 200 }}
                            className="bg-gradient-to-br from-[#01CC54] to-[#01CC54]/90 text-white rounded-full p-1.5 shadow-xl ring-4 ring-[#01CC54]/20"
                          >
                            <Check size={18} />
                          </motion.div>
                        )}
                      </div>

                      {/* Plan Name */}
                      <h3 className="text-lg font-extrabold text-gray-900 mb-3">{plan.name}</h3>

                      {/* Price Section */}
                      <div className="mb-4">
                        <div className="flex items-baseline mb-1.5">
                          <span className="text-3xl font-black text-[#01CC54] leading-none">
                            {plan.displayAmount}
                          </span>
                          <span className="text-gray-600 ml-2 text-xs font-medium">
                            /{plan.duration} {plan.durationType}
                            {plan.duration > 1 ? "s" : ""}
                          </span>
                        </div>
                        {details.savings && (
                          <motion.div
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#01CC54]/15 to-[#01CC54]/10 text-[#01CC54] border-2 border-[#01CC54]/40 shadow-md"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.4 + index * 0.1, type: "spring" }}
                          >
                            <Star size={10} className="fill-current" />
                            Save {details.savings}
                          </motion.div>
                        )}
                      </div>

                      {/* Features List */}
                      <ul className="space-y-2 mb-3">
                        {details.features.map((feature, featureIndex) => (
                          <motion.li
                            key={featureIndex}
                            className="flex items-start text-xs text-gray-700"
                            initial={{ opacity: 0, x: -15 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 + featureIndex * 0.05 }}
                          >
                            <div className="rounded-full p-1 mr-2 flex-shrink-0 bg-gradient-to-br from-[#01CC54]/20 to-[#01CC54]/10 border-2 border-[#01CC54]/40 shadow-sm mt-0.5">
                              <Check size={12} className="text-[#01CC54] font-bold" />
                            </div>
                            <span className="leading-relaxed font-medium">{feature}</span>
                          </motion.li>
                        ))}
                      </ul>

                      {/* Selected Overlay Effect */}
                      {isSelected && (
                        <motion.div
                          className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#01CC54]/5 to-transparent pointer-events-none"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Security & Trust Section */}
            <motion.div
              className="rounded-2xl p-4 mb-4 border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, type: "spring" }}
            >
              <div className="flex items-center justify-center mb-4">
                <div className="rounded-full p-2 bg-gradient-to-br from-[#01CC54]/20 to-[#01CC54]/10 border-2 border-[#01CC54]/30 mr-2.5">
                  <Shield size={20} className="text-[#01CC54]" />
                </div>
                <h3 className="text-base font-bold text-gray-900">
                  Secure & Trusted Payment
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: Shield, title: "SSL Encrypted", desc: "Bank-level security" },
                  { icon: Check, title: "PCI Compliant", desc: "Industry standard" },
                  { icon: Zap, title: "Instant Activation", desc: "Instance ready immediately" },
                  { icon: CreditCard, title: "Multiple Payment", desc: "Cards, UPI, Netbanking" },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="text-center p-2.5 rounded-xl bg-white border border-gray-200 hover:border-[#01CC54]/40 hover:shadow-md transition-all"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 + index * 0.1, type: "spring" }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="rounded-full p-2 w-fit mx-auto mb-2 bg-gradient-to-br from-[#01CC54]/20 to-[#01CC54]/10 border-2 border-[#01CC54]/30 shadow-sm">
                      <item.icon size={20} className="text-[#01CC54]" />
                    </div>
                    <h4 className="font-bold text-xs text-gray-900 mb-0.5">
                      {item.title}
                    </h4>
                    <p className="text-xs text-gray-600 font-medium">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Instance Summary */}
            <motion.div
              className="rounded-2xl p-3 mb-4 border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50/50 shadow-md"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, type: "spring" }}
            >
              <div className="flex items-center mb-3">
                <div className="rounded-full p-1.5 bg-[#01CC54]/10 border-2 border-[#01CC54]/30 mr-2.5">
                  <Smartphone size={18} className="text-[#01CC54]" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Instance Summary</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-white border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1.5">Instance Name</p>
                  <p className="font-bold text-base text-gray-900">"{instanceName}"</p>
                </div>
                <div className="p-3 rounded-xl bg-white border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-1.5">Type</p>
                  <p className="font-bold text-base text-gray-900">WhatsApp Business API</p>
                </div>
              </div>
            </motion.div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between pt-4 pb-3 border-t-2 border-gray-200 gap-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-5 py-2 text-gray-700 hover:text-[#01CC54] transition disabled:opacity-50 font-semibold focus:outline-none focus:ring-2 focus:ring-[#01CC54]/30 rounded-xl border-2 border-gray-200 hover:border-[#01CC54]/40 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>

              <div className="flex items-center gap-3">
                {selectedPlanData && (
                  <motion.div
                    className="text-right px-3 py-2 rounded-xl bg-gradient-to-br from-[#01CC54]/10 to-[#048AAD]/10 border-2 border-[#01CC54]/30"
                    initial={{ opacity: 0, x: 12, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ type: "spring" }}
                  >
                    <p className="text-xs font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">Total Amount</p>
                    <p className="text-xl font-black text-[#01CC54] leading-none">
                      {selectedPlanData.displayAmount || "—"}
                    </p>
                    <p className="text-xs text-gray-600 font-medium mt-0.5">{selectedPlanData.name}</p>
                  </motion.div>
                )}

                <motion.button
                  onClick={handlePayment}
                  disabled={
                    isProcessing ||
                    !selectedPlan ||
                    isLoading ||
                    (!razorpayLoaded && !isFreeTrialSelected) ||
                    !instanceName
                  }
                  className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center shadow-xl text-sm text-white bg-gradient-to-r from-[#048AAD] to-[#048AAD]/90 hover:from-[#048AAD]/90 hover:to-[#048AAD] focus:outline-none focus:ring-4 focus:ring-[#048AAD]/40 ${
                    isProcessing ||
                    isLoading ||
                    (!razorpayLoaded && !isFreeTrialSelected) ||
                    !instanceName
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:shadow-2xl hover:scale-105"
                  }`}
                  whileHover={
                    !isProcessing &&
                    !isLoading &&
                    (razorpayLoaded || isFreeTrialSelected) &&
                    instanceName
                      ? { scale: 1.05, y: -2 }
                      : {}
                  }
                  whileTap={
                    !isProcessing &&
                    !isLoading &&
                    (razorpayLoaded || isFreeTrialSelected) &&
                    instanceName
                      ? { scale: 0.95 }
                      : {}
                  }
                >
                  {isProcessing ? (
                    <>
                      <Loader className="animate-spin h-4 w-4 mr-2" />
                      Renewing Instance...
                    </>
                  ) : !isFreeTrialSelected && !razorpayLoaded ? (
                    <>
                      <Loader className="animate-spin h-4 w-4 mr-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} className="mr-1.5" />
                      {isFreeTrialSelected ? "Start Free Trial" : "Pay & Renew Instance"}
                    </>
                  )}
                </motion.button>
              </div>
            </div>

            {/* Terms */}
            <motion.p
              className="text-center text-xs text-gray-500 mt-4 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
            >
              By proceeding, you agree to our{" "}
              <a href="#" className="text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-2">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="#" className="text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-2">
                Privacy Policy
              </a>
              . Your payment is processed securely by Razorpay. Renewal takes effect immediately after successful payment.
            </motion.p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RenewalPaymentModal;
